import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CommonDataGrid from "../../../components/datagrid/CommonDataGrid";
import { useDispatch } from "react-redux";
import {
  deletePurchase,
  getPurchasesWithPagination,
  removePurchasePermanently,
} from "../../../redux/purchase/purchaseSlice";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import toast from "react-hot-toast";
import moment from "moment";
import { PurchaseStatusEnum, ROWS_PER_PAGE } from "../../../actionTypes";
import { useTranslation } from "react-i18next";
import { Input } from "antd";

const PurchaseList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = ROWS_PER_PAGE;
  const [anchorElMap, setAnchorElMap] = useState({});
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [search, setSearch] = useState("");
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [purchaseToPermanentlyDelete, setPurchaseToPermanentlyDelete] = useState(null);
  const [isRemovingPermanently, setIsRemovingPermanently] = useState(false);

  const handleClick = (event, rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: event.currentTarget,
    }));
  };

  const handleClose = (rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: null,
    }));
  };

  const handleRemovePermanentlyClick = (rowData) => {
    handleClose(rowData);
    setPurchaseToPermanentlyDelete(rowData);
    setPermanentDeleteDialogOpen(true);
  };

  const renderActionsColumn = (rowData) => {
    return (
      <>
        <IconButton onClick={(event) => handleClick(event, rowData)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorElMap[rowData.id]}
          open={Boolean(anchorElMap[rowData.id])}
          keepMounted
          onClose={() => handleClose(rowData)}
          sx={{
            boxShadow:
              "0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%), 0px 5px 22px 4px rgb(0 0 0 / 12%)",
          }}
        >
          <MenuItem
            sx={{ fontSize: "14px" }}
            onClick={() => handleEditClick(rowData.id)}
          >
            {t("Edit")}
          </MenuItem>
          {rowData.purchase_status_id !=
            PurchaseStatusEnum.Received.toString() && (
            <MenuItem
              sx={{ fontSize: "14px" }}
              onClick={() => handleDeleteClick(rowData.id)}
            >
              {t("Delete")}
            </MenuItem>
          )}
          <MenuItem
            sx={{ fontSize: "14px", color: "#dc2626", fontWeight: 500 }}
            onClick={() => handleRemovePermanentlyClick(rowData)}
          >
            {t("Remove Permanantly")}
          </MenuItem>
        </Menu>
      </>
    );
  };
  const columns = [
    { columnName: "batch_number", columnShow: "batch_number" },
    { columnName: "purchase_date", columnShow: "purchase_date" },
    { columnName: "product_name", columnShow: "product_name" },
    { columnName: "weight", columnShow: "weight", width: "300px !important" },
    { columnName: "wirehouse_name", columnShow: "wirehouse_name" },
    { columnName: "supplier_name", columnShow: "supplier_name" },
    {
      columnName: "actions",
      columnShow: "Actions",
      render: renderActionsColumn,
    },
  ];

  const cellFormatting = {
    batch_number: {
      textAlign: "left",
    },
    purchase_date: {
      textAlign: "left",
    },
    actions: {
      width: "100px",
      textAlign: "center",
    },
  };
  const manipulateData = (data) => {
    // Perform data manipulation here
    return data.map((item) => ({
      ...item,
      purchase_date: moment(item.purchase_date).format("DD-MMM-YYYY"),
    }));
  };

  const handleEditClick = (id) => {
    navigate(`/purchase/${id}`, { replace: true });
  };

  const handleDeleteClick = async (id) => {
    const res = await dispatch(deletePurchase(id))
      .then((response) => {
        if (
          response &&
          response.payload &&
          response.payload.statusCode === 200
        ) {
          toast.success(
            response && response.payload && response.payload.message
          );
          setDeleteStatus(true);
          //global?.window?.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        toast.error(error);
      });
  };

  const handleConfirmRemovePermanently = async () => {
    if (!purchaseToPermanentlyDelete) return;
    try {
      setIsRemovingPermanently(true);
      const response = await dispatch(
        removePurchasePermanently(purchaseToPermanentlyDelete.id)
      );
      if (
        response &&
        response.payload &&
        (response.payload.statusCode === 200 || response.payload.status === 200)
      ) {
        toast.success(
          (response.payload && response.payload.message) ||
            "Purchase and all related entries removed permanently!"
        );
        setPermanentDeleteDialogOpen(false);
        setPurchaseToPermanentlyDelete(null);
        window.location.reload();
      } else {
        const errorMsg =
          (response && response.payload && response.payload.message) ||
          (response && response.error && response.error.message) ||
          "Failed to remove purchase permanently";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error removing purchase permanently:", error);
      toast.error("Failed to remove purchase permanently");
    } finally {
      setIsRemovingPermanently(false);
    }
  };

  const fetchData = async (page = 0, pageSize = rowsPerPage) => {
    const response = await dispatch(
      getPurchasesWithPagination({
        tableName: "purchase_master",
        page: page - 1,
        pageSize: pageSize,
        search: search,
      })
    );

    const data = (await response.payload) && response.payload.data;
    return {
      data: (data && data.rows) || [],

      total: (data && data.total.total) || 0,
      totalPages: (data && Math.ceil(data.total.total / rowsPerPage)) || 0,
    };
  };
  useEffect(() => {
    if (deleteStatus) {
      fetchData(1); // Fetch data again after deletion
      setDeleteStatus(false); // Reset delete status
    }
  }, [deleteStatus]);
  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-6">
        <Typography variant="h6">{t("Purchase List")}</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/purchase/new", { replace: true })}
        >
          {t("Add Purchase")}
        </Button>
      </div>
      <Input
        className="mb-6 max-w-sm"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search purchase by product name..."
      />
      <CommonDataGrid
        columns={columns}
        fetchData={fetchData}
        cellFormatting={cellFormatting}
        dataManipulator={manipulateData}
      />

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => !isRemovingPermanently && setPermanentDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#b91c1c" }}>
          {t("Remove Purchase Permanently")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {t("Are you sure you want to permanently remove purchase")}{" "}
            <strong>{purchaseToPermanentlyDelete?.batch_number}</strong>
            {purchaseToPermanentlyDelete?.product_name
              ? ` (${purchaseToPermanentlyDelete.product_name})`
              : ""}?
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t(
              "All related entries for this purchase will be permanently removed, including stock entries, purchase items, and transaction movements. This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPermanentDeleteDialogOpen(false)}
            disabled={isRemovingPermanently}
            variant="outlined"
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmRemovePermanently}
            disabled={isRemovingPermanently}
            variant="contained"
            color="error"
            sx={{
              backgroundColor: "#dc2626",
              "&:hover": { backgroundColor: "#b91c1c" },
            }}
          >
            {isRemovingPermanently ? t("Removing...") : t("Remove Permanantly")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PurchaseList;
